import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react'
import {
  faBars,
  faChartLine,
  faInfoCircle, // Use info circle icon
  faSearch,
  faShoppingCart,
  faUser,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Badge,
  Button,
  Drawer,
  Group,
  Image,
  Indicator,
  Select,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Logo from '../assets/HiddenGemzLogo.png'
import { loginRequest } from '../authConfig'
import { useSearch } from '../contexts/CityFilterContext'
import { useNotifications } from '../contexts/NotificationContext'
import { useSessionInfo } from '../contexts/SessionContext'
import { CityDto, fetchCityStateCombinations } from '../data/location.service'
import { fetchMyOrganization } from '../data/organization.service'
import { OnboardingOrganization } from '../models/Onboarding/OnboardingOrganization'
import { isUserAdmin } from '../utils/authUtils'
import { COLORS } from '../utils/constants'
import classes from './css/TopMenu.module.css'
import DashboardMenu from './dashboard/DashboardMenu'
import NotificationBadge from './NotificationBadge'

export const TopMenu = () => {
  const { session } = useSessionInfo()
  const { selectedCityFilter, setSelectedCityFilter, setSearchTriggered } =
    useSearch()
  const { unreadCount } = useNotifications()
  const navigate = useNavigate()
  const location = useLocation()
  const { instance } = useMsal()
  const [cityOptions, setCityOptions] = useState<CityDto[]>([])
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [organization, setOrganization] =
    useState<OnboardingOrganization | null>(null)

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const cities = await fetchCityStateCombinations()
        setCityOptions(cities)
      } catch (error) {
        console.error('Error fetching city, state combinations:', error)
      }
    }
    fetchCities()
  }, [])

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const org = await fetchMyOrganization(instance)
        setOrganization(org)
      } catch (error) {
        console.error('Error fetching organization:', error)
      }
    }

    if (session) {
      fetchOrganization()
    }
  }, [instance, session])

  const handleLoginRedirect = () => {
    instance.loginRedirect(loginRequest).catch((error) => console.log(error))
  }

  const handleCityFilterChange = (city: string | null) => {
    setSelectedCityFilter(city)
  }

  const handleMenuClick = () => {
    setIsMobileMenuOpen(true)
  }

  const handleSearch = () => {
    setSearchTriggered(true)
  }

  return (
    <Group className={classes.container} justify='space-between'>
      <UnstyledButton
        onClick={() => {
          navigate('/')
        }}
      >
        <Image src={Logo} style={{ width: 80 }}></Image>
      </UnstyledButton>

      {location.pathname === '/' && (
        <Select
          visibleFrom='sm'
          size='md'
          placeholder='Select a city'
          value={selectedCityFilter}
          onChange={handleCityFilterChange}
          data={[
            { label: 'Anywhere', value: '' },
            ...cityOptions.map((city) => ({
              label: `${city.name}, ${city.state}`,
              value: `${city.name}, ${city.state}`,
            })),
          ]}
          searchable
          allowDeselect={false}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch()
            }
          }}
          nothingFoundMessage='No screens in this city yet'
          rightSection={
            <FontAwesomeIcon
              icon={faSearch}
              color='white'
              size='xs'
              onClick={handleSearch}
              style={{
                cursor: 'pointer',
                backgroundColor: COLORS.HiddenGemzRed,
                padding: 8,
                borderRadius: 30,
              }}
            />
          }
          rightSectionPointerEvents='auto'
          style={{ width: 300, borderRadius: '50px' }}
          styles={{
            input: {
              borderRadius: '50px',
              paddingLeft: '20px',
              paddingRight: '20px',
            },
          }}
        />
      )}

      {/* Main navigation group, always show About/info circle as left-most icon */}
      <Group align='center'>
        <AuthenticatedTemplate>
          {!session?.isHostOnboardingComplete && (
            <Button
              size='xs'
              variant='default'
              onClick={() => navigate('/hostOnboarding')}
              visibleFrom='sm'
            >
              {session?.isHostOnboardingStarted
                ? 'Complete Business Profile'
                : 'Become a Host'}
            </Button>
          )}
        </AuthenticatedTemplate>

        {/* About us icon - always visible */}
        <UnstyledButton
          onClick={() => navigate('/about')}
          style={{ position: 'relative' }}
          title='About us'
          aria-label='About us'
        >
          <Stack gap={0}>
            <FontAwesomeIcon
              icon={faInfoCircle}
              className={classes.accountIcon}
              style={{ color: 'white' }}
              size='xl'
            />
            <Text
              visibleFrom='sm'
              style={{ color: 'white', fontSize: 10, textAlign: 'center' }}
            >
              About us
            </Text>
          </Stack>
        </UnstyledButton>

        <AuthenticatedTemplate>
          {isUserAdmin(session?.user?.id) && (
            <UnstyledButton
              onClick={() => navigate('admin')}
              style={{ position: 'relative' }}
            >
              <Stack gap={0}>
                <FontAwesomeIcon
                  icon={faUser}
                  className={classes.accountIcon}
                  style={{ color: 'white' }}
                  size='xl'
                />
                <Text
                  visibleFrom='sm'
                  style={{ color: 'white', fontSize: 10, textAlign: 'center' }}
                >
                  Admin
                </Text>
              </Stack>
            </UnstyledButton>
          )}

          {/* Notifications */}
          <NotificationBadge />

          <UnstyledButton
            onClick={() => navigate('/cart')}
            style={{ position: 'relative' }}
          >
            <Stack gap={0}>
              <FontAwesomeIcon
                icon={faShoppingCart}
                className={classes.accountIcon}
                style={{ color: 'white' }}
                size='xl'
              />
              <Text
                visibleFrom='sm'
                style={{ color: 'white', fontSize: 10, textAlign: 'center' }}
              >
                Cart
              </Text>
              {session && (session.itemsInCart.length + (session.allLocationsBundleCount ?? 0)) > 0 && (
                <Badge
                  circle
                  color={COLORS.HiddenGemzRed}
                  size='md'
                  style={{
                    border: '2px solid #16503A',
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    transform: 'translate(50%, -50%)',
                  }}
                >
                  {session.itemsInCart.length + (session.allLocationsBundleCount ?? 0)}
                </Badge>
              )}
            </Stack>
          </UnstyledButton>

          {/* Dashboard button for desktop */}
          <UnstyledButton
            visibleFrom='sm'
            onClick={() => navigate('dashboard')}
          >
            <Stack gap={0}>
              <FontAwesomeIcon
                icon={faChartLine}
                className={classes.accountIcon}
                style={{ color: 'white', margin: 10 }}
                size='xl'
              />
              <Text
                style={{ color: 'white', fontSize: 10, textAlign: 'center' }}
              >
                Dashboard
              </Text>
            </Stack>
          </UnstyledButton>

          {/* Hamburger menu for mobile */}
          <Indicator
            disabled={unreadCount === 0}
            color={COLORS.HiddenGemzRed}
            size={12}
            position='top-end'
            offset={10}
            hiddenFrom='sm'
          >
            <UnstyledButton hiddenFrom='sm' onClick={handleMenuClick}>
              <Stack gap={0}>
                <FontAwesomeIcon
                  icon={faBars}
                  className={classes.accountIcon}
                  style={{ color: 'white', margin: 10 }}
                  size='xl'
                />
              </Stack>
            </UnstyledButton>
          </Indicator>
        </AuthenticatedTemplate>

        <UnauthenticatedTemplate>
          <Button
            onClick={handleLoginRedirect}
            style={{ backgroundColor: COLORS.HiddenGemzRed }}
          >
            Log in
          </Button>
        </UnauthenticatedTemplate>
      </Group>
      {/* Mobile Menu Drawer */}
      <Drawer
        opened={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        position='right'
        size='100%'
        hiddenFrom='sm'
        title={
          <Stack gap={0}>
            <Text c='white' fz={10}>
              ORGANIZATION
            </Text>
            <Text c='white'>{organization?.name || 'Menu'}</Text>
          </Stack>
        }
        styles={{
          body: {
            height: '100%',
            padding: 0,
          },
          content: {
            height: '100%',
          },
          inner: {
            height: '100%',
          },
          header: {
            backgroundColor: COLORS.LightGreen,
            borderBottom: 'none',
            padding: '16px 24px',
          },
          close: {
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          },
          title: {
            color: 'white',
            fontSize: '1.2rem',
            fontWeight: 500,
          },
        }}
      >
        <DashboardMenu
          organization={organization}
          onClose={() => setIsMobileMenuOpen(false)}
        />
      </Drawer>
    </Group>
  )
}
